/**
 * Pure composer model for the Prompt Studio (lot B step 2).
 * No React, no API calls.
 *
 * @see docs/plans/prompt-studio-build/B-popup-frontend.md §Files and props composerModel.ts
 */

import type { BrandQueryAxis, BrandQueryIntent, BrandQuerySource, BulkClassified, BulkRequest, TargetZone } from '@traaker/shared';
import type { LocKey, CellTriple } from './promptStudioModel';
import type { ReviewRow } from './studioReducer';

// ---------------------------------------------------------------------------
// ComposerSource / SOURCE_TO_BULK
// ---------------------------------------------------------------------------

/** The composer input sources (`persona`: owner 2026-11-02, generation by Brand Knowledge persona). */
export type ComposerSource = 'write' | 'gen' | 'persona' | 'kw' | 'csv' | 'gsc';

/** Maps a ComposerSource to the BrandQuerySource sent in the bulk request. */
export const SOURCE_TO_BULK: Record<ComposerSource, BrandQuerySource> = {
  write: 'user_added',
  gen:   'geo_strategy_inferred',
  // Generated too: no BrandQuerySource of its own (a new value would be an API + shared type change).
  persona: 'geo_strategy_inferred',
  kw:    'keywords',
  csv:   'csv_import',
  gsc:   'search_console',
};

// ---------------------------------------------------------------------------
// GenState / seedFromCell / genTargets
// ---------------------------------------------------------------------------

/**
 * State for the Générer band.
 * Order in the band: axis → competitors (Comparaison only) → vocabulary → language → count → Générer
 * (PersonaBand: persona → language → count).
 */
export interface GenState {
  axis:      BrandQueryAxis;
  terms:     (string | null)[];
  /** Comparaison: the competitor names to compare the brand with; [] = all of them. */
  competitors: string[];
  /** The PersonaBand's persona (the « Persona » source; Générer ignores it). */
  personaId: string | null;
  count:     3 | 5 | 10;
  loc:       LocKey;
  /** The ONE language the proposals are written in (owner, 2026-10-20); default the brand working language. */
  language:  string;
}

/**
 * The ONLY seeding function (mock `seedGen`).
 * Seeds the Générer band from a hatched Couverture cell.
 */
export function seedFromCell(c: CellTriple, language: string): GenState {
  return {
    axis:      c.ax,
    terms:     c.tp !== null ? [c.tp] : [],
    competitors: [],
    personaId: null,
    count:     5,
    loc:       c.loc,
    language,
  };
}

/**
 * Build the cell triples this GenState targets.
 *
 * terms [] → one triple per vocab term;
 * empty vocab → [{ loc, ax, tp: null }] (one triple, never zero).
 */
export function genTargets(g: GenState, vocab: string[]): CellTriple[] {
  const terms = g.terms.filter((t): t is string => t !== null);
  if (terms.length > 0) {
    return terms.map((tp) => ({ loc: g.loc, ax: g.axis, tp }));
  }
  if (vocab.length > 0) {
    return vocab.map((tp) => ({ loc: g.loc, ax: g.axis, tp }));
  }
  return [{ loc: g.loc, ax: g.axis, tp: null }];
}

// ---------------------------------------------------------------------------
// Drafts
// ---------------------------------------------------------------------------

/**
 * ONE draft per source.  Switching source keeps each draft;
 * « Vérifier » sends the CURRENT source's draft only.
 * Générer / Persona (editable, then « Vérifier », owner 2026-11-02) and Mots clés REPLACE their own source's draft;
 * a CSV file APPENDS to the CSV draft.
 */
export type Drafts = Record<ComposerSource, string>;

// ---------------------------------------------------------------------------
// draftLines / overCapLines / MAX_SEEDS / chunk
// ---------------------------------------------------------------------------

/**
 * Split a draft string into individual prompt lines.
 * Split on \n, trim each line, drop empties.  Never rewrites the draft.
 */
export function draftLines(draft: string): string[] {
  return draft.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
}

/**
 * Count lines that exceed 300 characters (C3 cap).
 * Flagged amber in the UI; still sent (the review row shows the limit).
 */
export function overCapLines(lines: string[]): number {
  return lines.filter((l) => l.length > 300).length;
}

/** Maximum keywords for the Mots clés source (C12). */
export const MAX_SEEDS = 50;

/**
 * Split an array into chunks of size `n` (default 100).
 * The last chunk may be smaller.
 */
export function chunk<T>(xs: T[], n = 100): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < xs.length; i += n) {
    result.push(xs.slice(i, i + n));
  }
  return result;
}

// ---------------------------------------------------------------------------
// classifyDraft — « Vérifier » (step 5)
// ---------------------------------------------------------------------------

export function toReviewRow(c: BulkClassified): ReviewRow {
  return {
    text: c.text,
    axis: c.axis,
    intent: c.intent,
    language: c.language,
    complements: c.complements,
    topic: c.topic,
    criteria: c.criteria,
    targetId: c.targetId,
    targetCategory: c.targetCategory,
    targetLookup: c.targetLookup,
  };
}

/**
 * The classification dryRun of « Vérifier » and of a review row's « réessayer »:
 * `zone` is the batch place (the composer's, or the retried row's; null = Mondial).
 */
export function classifyRequest(
  texts: string[],
  source: ComposerSource,
  zone: TargetZone | null,
): BulkRequest {
  return { texts, dryRun: true, source: SOURCE_TO_BULK[source], defaults: { zoneMeta: zone } };
}

export type DraftClassification =
  | { rows: ReviewRow[]; rateLimited: number }
  | { error: 'rate_limited' | 'failed' };

/**
 * Lines per « Vérifier » request. The server classifies 12 lines at a time inside one request, so
 * 25 costs about what 100 did and lets the progress bar move every 25 lines (owner 2026-11-04: a
 * CSV of 100 lines was one silent call). The rate limit counts lines, not requests.
 */
export const CLASSIFY_CHUNK = 25;

/**
 * Classify draft lines in sequential chunks of `CLASSIFY_CHUNK` (dryRun, saves nothing),
 * reporting `onProgress(classified, total)` before the first chunk and after each one.
 * A 429 on a LATER chunk keeps the classified rows and turns every remaining
 * line into a row with no intent (« en attente »); a 429 on the first chunk,
 * or any other failure, is an error and the draft is kept.
 */
export async function classifyDraft(
  lines: string[],
  classify: (texts: string[]) => Promise<BulkClassified[]>,
  onProgress?: (done: number, total: number) => void,
): Promise<DraftClassification> {
  const chunks = chunk(lines, CLASSIFY_CHUNK);
  const rows: ReviewRow[] = [];
  onProgress?.(0, lines.length);
  for (let i = 0; i < chunks.length; i++) {
    try {
      rows.push(...(await classify(chunks[i])).map(toReviewRow));
      onProgress?.(rows.length, lines.length);
    } catch (err) {
      const rateLimited = (err as { status?: number })?.status === 429;
      if (i === 0 || !rateLimited) return { error: rateLimited ? 'rate_limited' : 'failed' };
      const rest = chunks.slice(i).flat();
      return { rows: [...rows, ...rest.map((text) => ({ text }))], rateLimited: rest.length };
    }
  }
  return { rows, rateLimited: 0 };
}

// ---------------------------------------------------------------------------
// Cooldown
// ---------------------------------------------------------------------------

/** Generation cooldown duration in milliseconds. */
export const GEN_COOLDOWN_MS = 10_000;

/**
 * Seconds remaining before the generation button is re-enabled.
 * ceil, ≥ 0.  Returns 0 when `until` is null or already passed.
 */
export function cooldownSecondsLeft(until: number | null, now: number): number {
  if (until === null) return 0;
  return Math.max(0, Math.ceil((until - now) / 1000));
}

// ---------------------------------------------------------------------------
// intentForAxis
// ---------------------------------------------------------------------------

/**
 * The default intent for an axis, given the row's previous intent.
 *
 *   authority  → 'learn'
 *   referral   → 'recommend'
 *   comparison → 'compare'
 *   narrative  → prev if it is 'describe' | 'reputation', else 'describe'
 */
export function intentForAxis(
  ax: BrandQueryAxis,
  prev: BrandQueryIntent | undefined,
): BrandQueryIntent {
  if (ax === 'authority')  return 'learn';
  if (ax === 'referral')   return 'recommend';
  if (ax === 'comparison') return 'compare';
  // narrative
  if (prev === 'describe' || prev === 'reputation') return prev;
  return 'describe';
}
